import { useCallback, useContext, useEffect, useState } from 'react';
import SettingSectionCodeArea from '../../../components/settings/SettingSectionCodeArea';
import SettingSectionTextField from '../../../components/settings/SettingSectionTextField';
import SettingSectionTitle from '../../../components/settings/SettingSectionTitle';
import { AuthContext } from '../../../contexts/AuthContext';
import { DashboardContext } from '../../../contexts/DashboardContext';
import { SnackbarContext } from '../../../contexts/SnackbarContext';
import { UserContext } from '../../../contexts/UserContext';
import { updateDynamicPage } from '../../../services/DynamicPagesService';
import { ManageDynamicPageCustomCodes } from './ManageDynamicPageCustomCodes';

export const ManageDynamicPageSettingsCustomCode = () => {
  const { show } = useContext(SnackbarContext);
  const { isSupportAdmin } = useContext(AuthContext);
  const { selectedAccount } = useContext(UserContext);
  const { selectedDynamicPage } = useContext(DashboardContext);

  const [headCustomCode, setHeadCustomCode] = useState<string>('');
  const [headCustomCodeDelay, setHeadCustomCodeDelay] = useState<number>(0);
  const [bodyCustomCode, setBodyCustomCode] = useState<string>('');
  const [bodyCustomCodeDelay, setBodyCustomCodeDelay] = useState<number>(0);

  useEffect(() => {
    if (!selectedDynamicPage) return;

    setHeadCustomCode(
      selectedDynamicPage?.data()?.config?.headCustomCode || '',
    );
    setHeadCustomCodeDelay(
      selectedDynamicPage?.data()?.config?.headCustomCodeDelay || 0,
    );
    setBodyCustomCode(
      selectedDynamicPage?.data()?.config?.bodyCustomCode || '',
    );
    setBodyCustomCodeDelay(
      selectedDynamicPage?.data()?.config?.bodyCustomCodeDelay || 0,
    );
  }, [selectedDynamicPage]);

  const updateDynamicPageData = useCallback(
    async (data: any) => {
      if (!selectedAccount || !selectedDynamicPage) return;
      try {
        await updateDynamicPage(
          selectedAccount.id,
          selectedDynamicPage.id,
          data,
        );
        show('Updated');
      } catch (_) {
        show('Failed to update');
      }
    },
    [selectedAccount, selectedDynamicPage, show],
  );

  const updateHeadCustomCode = useCallback(async () => {
    if (!selectedDynamicPage) return;
    if (selectedDynamicPage.data().config?.headCustomCode === headCustomCode) {
      return;
    }

    updateDynamicPageData({
      config: {
        ...selectedDynamicPage.data().config,
        headCustomCode,
      },
    });
  }, [selectedDynamicPage, headCustomCode, updateDynamicPageData]);

  const updateBodyCustomCode = useCallback(async () => {
    if (!selectedDynamicPage) return;
    if (selectedDynamicPage.data().config?.bodyCustomCode === bodyCustomCode) {
      return;
    }

    updateDynamicPageData({
      config: {
        ...selectedDynamicPage.data().config,
        bodyCustomCode,
      },
    });
  }, [selectedDynamicPage, bodyCustomCode, updateDynamicPageData]);

  const updateHeadCustomCodeDelay = useCallback(async () => {
    if (!selectedDynamicPage) return;
    if (
      selectedDynamicPage.data().config?.headCustomCodeDelay ===
      headCustomCodeDelay
    ) {
      return;
    }

    updateDynamicPageData({
      config: {
        ...selectedDynamicPage.data().config,
        headCustomCodeDelay,
      },
    });
  }, [selectedDynamicPage, headCustomCodeDelay, updateDynamicPageData]);

  const updateBodyCustomCodeDelay = useCallback(async () => {
    if (!selectedDynamicPage) return;
    if (
      selectedDynamicPage.data().config?.bodyCustomCodeDelay ===
      bodyCustomCodeDelay
    ) {
      return;
    }

    updateDynamicPageData({
      config: {
        ...selectedDynamicPage.data().config,
        bodyCustomCodeDelay,
      },
    });
  }, [selectedDynamicPage, bodyCustomCodeDelay, updateDynamicPageData]);

  return (
    <div>
      <SettingSectionTitle
        title="Custom code"
        description="Add custom code to all of your landing pages."
      />

      <dl className="mt-6 divide-y divide-gray-100 border-t border-gray-200 text-sm leading-6 [&>*]:pb-6">
        <SettingSectionCodeArea
          title="Head"
          value={headCustomCode}
          setValue={setHeadCustomCode}
          update={updateHeadCustomCode}
          editable={true}
        />
        {isSupportAdmin && (
          <SettingSectionTextField
            title="Head Delay"
            value={headCustomCodeDelay + ''}
            setValue={(value: any) => {
              setHeadCustomCodeDelay(Number.isNaN(+value) ? 0 : +value);
            }}
            update={updateHeadCustomCodeDelay}
            pattern="^\d*$"
            editable
          />
        )}
        <SettingSectionCodeArea
          title="Body"
          value={bodyCustomCode}
          setValue={setBodyCustomCode}
          update={updateBodyCustomCode}
          editable={true}
        />
        {isSupportAdmin && (
          <SettingSectionTextField
            title="Body Delay"
            value={bodyCustomCodeDelay + ''}
            setValue={(value: any) => {
              setBodyCustomCodeDelay(Number.isNaN(+value) ? 0 : +value);
            }}
            update={updateBodyCustomCodeDelay}
            pattern="^\d*$"
            editable
          />
        )}
      </dl>

      {/* New Multi-Code Management */}
      <div className="mt-12">
        <ManageDynamicPageCustomCodes />
      </div>
    </div>
  );
};
